<template>
  <div class="recycle-bin-container">
    <!-- 页面标题和操作区 -->
    <div class="page-header">
      <el-button 
          icon="el-icon-back" 
          type="text" 
          @click="goBack"
          class="back-btn"
        >
          返回上一页
      </el-button>
      <h2>回收站</h2>
      <div class="action-buttons">
        <el-button 
          type="danger" 
          icon="el-icon-delete" 
          @click="handleBatchDelete"
          :disabled="selectedFiles.length === 0"
        >
          彻底删除所选
        </el-button>
        <el-button 
          type="primary" 
          icon="el-icon-refresh-left" 
          @click="handleBatchRestore"
          :disabled="selectedFiles.length === 0"
        >
          恢复所选
        </el-button>
      </div>
    </div>
    <div class="recycle-tip"><i class="el-icon-info"></i> 文件在回收站保留 7 天。恢复后可继续使用，彻底删除后将无法找回。</div>

    <!-- 筛选区域 -->
    <div class="filter-container card">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-input 
            v-model="searchText" 
            placeholder="搜索文件名" 
            prefix-icon="el-icon-search"
            clearable
            @clear="fetchFiles"
            @keyup.enter.native="fetchFiles"
          ></el-input>
        </el-col>
        <el-col :span="12">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            @change="fetchFiles"
          ></el-date-picker>
        </el-col>
      </el-row>
    </div>

    <!-- 文件列表区域 -->
    <div class="file-list-container card">
      <el-table 
        :data="fileList" 
        stripe 
        v-loading="loading"
        @selection-change="handleSelectionChange"
        :row-style="{ height: '50px' }"
        :header-cell-style="headerCellStyle"
        empty-text="回收站中暂无文件"
      >
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="fileName" label="文件名称" min-width="200"></el-table-column>
        <el-table-column label="文件大小" width="120">
          <template slot-scope="scope">
            {{ formatSize(scope.row.fileSize) }}
          </template>
        </el-table-column>
        <el-table-column prop="deleteTime" label="删除时间" width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.updateDate) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200">
          <template slot-scope="scope">
            <el-button 
              type="text" 
              icon="el-icon-refresh-left" 
              @click="handleRestore(scope.row)"
              size="small"
              title="恢复"
            ></el-button>
            <el-button 
              type="text" 
              icon="el-icon-delete" 
              @click="handleDelete(scope.row)"
              size="small"
              title="彻底删除"
              style="color: #ff4d4f"
            ></el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页控件 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.pageNum"
          :page-sizes="[5, 10, 20, 50]"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
        ></el-pagination>
      </div>
    </div>

    <!-- 空状态插图 -->
    <div v-if="fileList.length === 0 && !loading" class="empty-state">
      <div class="empty-icon">
        <i class="el-icon-empty"></i>
      </div>
      <div class="empty-text">回收站是空的</div>
      <div class="empty-desc">删除的文件将在这里保留30天</div>
    </div>
  </div>
</template>

<script>
import { getFileList } from '@/api/file';
import { restoreFile,deleteFile } from '@/api/recycleBin';

export default {
  name: 'RecycleBin',
  data() {
    return {
      fileList: [],
      loading: false,
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      searchText: '',
      selectedFiles: [],
      dateRange: []
    };
  },
  created() {
    this.fetchFiles();
  },
  methods: {
    goBack() {
      window.history.back();
    },
    fetchFiles() {
      this.loading = true;
      const params = {
        status: 0,
        searchText: this.searchText,
        startTime: this.dateRange[0] ? this.formatDateForApi(this.dateRange[0]) : null,
        endTime: this.dateRange[1] ? this.formatDateForApi(this.dateRange[1]) : null,
        pageNum: this.pagination.pageNum,
        pageSize: this.pagination.pageSize
      };

      getFileList(params).then(res => {
        this.fileList = res.data.records || [];
        this.pagination.total = res.data.total || 0;
      }).catch(err => {
        this.$message.error('获取文件失败: ' + (err.message || err));
      }).finally(() => {
        this.loading = false;
      });
    },

    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.pageNum = 1;
      this.fetchFiles();
    },

    handleCurrentChange(pageNum) {
      this.pagination.pageNum = pageNum;
      this.fetchFiles();
    },

    handleSelectionChange(selection) {
      this.selectedFiles = selection;
    },

    handleRestore(file) {
      this.$confirm(`确定要恢复文件"${file.fileName}"吗？`, '恢复确认', {
        type: 'info',
        confirmButtonText: '恢复',
        cancelButtonText: '取消'
      }).then(() => {
        restoreFile({
          fileIds: [file.fileId]
        }).then(() => {
          this.$message.success('文件已恢复');
          this.fetchFiles();
        }).catch(err => {
          this.$message.error('恢复失败: ' + (err.message || err));
        });
      });
    },

    handleDelete(file) {
      this.$confirm(`确定要彻底删除文件"${file.fileName}"吗？此操作不可恢复！`, '删除确认', {
        type: 'warning',
        confirmButtonText: '确认删除',
        cancelButtonText: '取消'
      }).then(() => {
        deleteFile({
          fileIds: [file.fileId]
        }).then(() => {
          this.$message.success('文件已彻底删除');
          this.fetchFiles();
        }).catch(err => {
          this.$message.error('删除失败: ' + (err.message || err));
        });
      });
    },

    handleBatchRestore() {
      if (this.selectedFiles.length === 0) {
        this.$message.warning('请先选择要恢复的文件');
        return;
      }

      this.$confirm(`确定要恢复选中的${this.selectedFiles.length}个文件吗？`, '批量恢复确认', {
        type: 'info',
        confirmButtonText: '恢复',
        cancelButtonText: '取消'
      }).then(() => {
        const ids = this.selectedFiles.map(file => file.fileId);
        restoreFile({
          fileIds: ids
        }).then(() => {
          this.$message.success('选中文件已恢复');
          this.fetchFiles();
          this.selectedFiles = [];
        }).catch(err => {
          this.$message.error('恢复失败: ' + (err.message || err));
        });
      });
    },

    handleBatchDelete() {
      if (this.selectedFiles.length === 0) {
        this.$confirm(`确定要清空回收站吗？此操作不可恢复！`, '清空回收站确认', {
          type: 'danger',
          confirmButtonText: '确认清空',
          cancelButtonText: '取消'
        }).then(() => {
          const ids = this.selectedFiles.map(file => file.fileId);
          deleteFile({
            fileIds: ids
          }).then(() => {
            this.$message.success('回收站已清空');
            this.fetchFiles();
            this.selectedFiles = [];
          }).catch(err => {
            this.$message.error('清空失败: ' + (err.message || err));
          });
        });
      } else {
        this.$confirm(`确定要彻底删除选中的${this.selectedFiles.length}个文件吗？此操作不可恢复！`, '批量删除确认', {
          type: 'warning',
          confirmButtonText: '确认删除',
          cancelButtonText: '取消'
        }).then(() => {
          const ids = this.selectedFiles.map(file => file.fileId);
          deleteFile({
            fileIds: ids
          }).then(() => {
            this.$message.success('选中文件已彻底删除');
            this.fetchFiles();
            this.selectedFiles = [];
          }).catch(err => {
            this.$message.error('删除失败: ' + (err.message || err));
          });
        });
      }
    },

    formatSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateForApi(date) {
      if (!date) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  },
  computed: {
    headerCellStyle() {
      return {
        'background-color': '#f5f7fa',
        'font-weight': '500',
        'color': '#303133'
      };
    }
  }
};
</script>

<style scoped>
.recycle-bin-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  color: #303133;
  margin: 0;
}

.filter-container {
  margin-bottom: 20px;
  padding: 20px;
}

.file-list-container {
  margin-bottom: 20px;
  padding: 20px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

/* 空状态样式 */
.empty-state {
  text-align: center;
  padding: 60px 0;
  color: #909399;
}

.empty-icon {
  font-size: 60px;
  margin-bottom: 20px;
  color: #c0c4cc;
}

.empty-text {
  font-size: 16px;
  margin-bottom: 10px;
}

.empty-desc {
  font-size: 14px;
  color: #c0c4cc;
}

/* 表格样式优化 */
::v-deep .el-table {
  border-radius: 8px;
  overflow: hidden;
}

::v-deep .el-table tr:hover > td {
  background-color: #f9fafc !important;
}

::v-deep .el-table__body tr.current-row > td {
  background-color: #f0f9eb !important;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .recycle-bin-container {
    padding: 10px;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .action-buttons {
    width: 100%;
    justify-content: space-between;
  }
  
  .filter-container, .file-list-container {
    padding: 15px;
  }
  
  .pagination-container {
    text-align: center;
  }
}
</style>
